import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Calendar,
  FileText,
  Gavel,
  ShieldCheck,
  Users,
  Settings,
  ScrollText,
  ClipboardList,
  LogOut,
  Bell,
  Menu,
  X,
  MessageSquare,
  GraduationCap,
  Award,
  UserPlus,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { ROLES } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

function navItemsFor(role) {
  switch (role) {
    case ROLES.COMERCIAL:
      return [
        { to: "/comercial", label: "Novo Cadastro", icon: Briefcase },
      ];
    case ROLES.SECRETARIA:
      return [
        { to: "/secretaria/dashboard", label: "Painel", icon: LayoutDashboard },
        { to: "/secretaria/novos-clientes", label: "Novos Clientes", icon: UserPlus },
        { to: "/secretaria/contratos", label: "Contratos", icon: FileText },
        { to: "/secretaria/agenda", label: "Agenda & Reuniões", icon: Calendar },
        { to: "/secretaria/providencias", label: "Providências", icon: ClipboardList },
        { to: "/secretaria/canal-diretora", label: "Canal com Diretora", icon: MessageSquare },
      ];
    case ROLES.CONSULTOR:
      return [
        { to: "/consultor/dashboard", label: "Painel", icon: LayoutDashboard },
        { to: "/consultor/contratos", label: "Meus Contratos", icon: FileText },
        { to: "/consultor/meu-ranking", label: "Meu Ranking", icon: Award },
        { to: "/consultor/treinamento", label: "Treinamento", icon: GraduationCap },
      ];
    case ROLES.JURIDICO:
      return [
        { to: "/juridico/dashboard", label: "Painel", icon: LayoutDashboard },
        { to: "/juridico/revisoes", label: "Fila de Revisões", icon: Gavel },
        { to: "/juridico/contratos", label: "Contratos sob Controle", icon: FileText },
        { to: "/juridico/ranking", label: "Ranking de Consultores", icon: Award },
      ];
    case ROLES.DIRETORA:
      return [
        { to: "/diretora/dashboard", label: "Painel Executivo", icon: LayoutDashboard },
        { to: "/diretora/novo-cadastro", label: "Novo Cadastro", icon: Briefcase },
        { to: "/diretora/agenda", label: "Agenda Google", icon: Calendar },
        { to: "/diretora/aprovacoes", label: "Aprovações", icon: ShieldCheck },
        { to: "/diretora/contratos", label: "Contratos", icon: FileText },
        { to: "/diretora/canal-secretaria", label: "Canal com Secretária", icon: MessageSquare },
        { to: "/diretora/ranking", label: "Ranking", icon: Award },
        { to: "/diretora/treinamentos", label: "Treinamentos", icon: GraduationCap },
        { to: "/diretora/produtos", label: "Produtos & Fluxos", icon: ScrollText },
        { to: "/diretora/usuarios", label: "Usuários", icon: Users },
        { to: "/diretora/configuracoes", label: "Configurações", icon: Settings },
      ];
    default:
      return [];
  }
}

const SidebarContent = ({ navItems, onItemClick }) => (
  <div className="h-full flex flex-col bg-ink text-slate-200">
    <div className="p-5 border-b border-white/10">
      <Logo height={56} bare />
      <div className="mt-3 text-[11px] uppercase tracking-wider text-slate-400">
        Gestão de Contratos
      </div>
    </div>
    <nav className="flex-1 px-3 py-4 space-y-1 scroll-thin overflow-y-auto">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onItemClick}
          end
          data-testid={`nav-${item.to.replace(/\//g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
              isActive
                ? "bg-white/10 text-white border-l-2 border-gold"
                : "text-slate-300 hover:bg-white/5 hover:text-white",
            )
          }
        >
          <item.icon className="w-4 h-4" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
    <div className="p-4 text-[11px] text-slate-500 border-t border-white/10">
      CEAP Brasil &amp; Lopes &middot; v1.0
    </div>
  </div>
);

export const AppShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = navItemsFor(user?.role);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const roleLabel = ({
    [ROLES.COMERCIAL]: "Comercial",
    [ROLES.SECRETARIA]: "Secretária",
    [ROLES.CONSULTOR]: "Consultor",
    [ROLES.JURIDICO]: "Controlador Jurídico",
    [ROLES.DIRETORA]: "Diretora",
  })[user?.role] || "Usuário";

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <SidebarContent navItems={navItems} />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <SidebarContent navItems={navItems} onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen((p) => !p)}
              data-testid="mobile-menu-button"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{roleLabel}</div>
              <div className="text-sm font-medium text-slate-800" data-testid="header-user-name">
                {user?.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-600 hover:text-slate-900"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
